import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, ExternalLink, Newspaper, Plus } from 'lucide-react';
import { usePosts } from '../hooks/usePosts';
import { saveSiteContent, savedSiteContent, useSiteContent } from '../hooks/useSiteContent';
import { useAuditLog } from '../hooks/useAuditLog';
import { isRichTextBlank } from '../lib/richText';
import { Post, POST_KIND_LABELS, PostInput, PostKind, postDateLabel } from '../types/post';
import { recipeSlug } from '../types/recipe';
import { ImageField } from './SiteImageFields';
import RichTextField from './RichTextField';
import ConfirmationModal from './utils/ConfirmationModal';

interface Props {
  /** executeProtectedAction della dashboard: verifica admin prima di salvare. */
  executeProtectedAction: <T>(action: () => Promise<T>) => Promise<T | null>;
}

const inputClass = 'h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground';
const textareaClass = 'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm leading-6 text-foreground';
const cardClass = 'overflow-hidden rounded-xl bg-card shadow-[0_4px_20px_-2px_rgba(180,83,9,.06)] ring-1 ring-border/40';
const primaryButton =
  'h-10 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60';
const ghostButton = 'h-10 rounded-lg px-4 text-[13px] font-semibold text-muted-foreground hover:bg-muted disabled:opacity-60';

/** Bozza del form: le date vuote restano stringhe finché non si salva. */
interface Draft {
  kind: PostKind;
  title: string;
  slug: string;
  /** Finché è false l'indirizzo segue il titolo; scrivendolo a mano si stacca. */
  slugTouched: boolean;
  summary: string;
  image_url: string | null;
  body: string;
  event_date: string;
  event_end_date: string;
  event_time: string;
  location: string;
  published: boolean;
}

const emptyDraft: Draft = {
  kind: 'evento',
  title: '',
  slug: '',
  slugTouched: false,
  summary: '',
  image_url: null,
  body: '',
  event_date: '',
  event_end_date: '',
  event_time: '',
  location: '',
  published: false,
};

const toDraft = (p: Post): Draft => ({
  kind: p.kind,
  title: p.title,
  slug: p.slug,
  slugTouched: true,
  summary: p.summary,
  image_url: p.image_url,
  body: p.body,
  event_date: p.event_date ?? '',
  event_end_date: p.event_end_date ?? '',
  event_time: p.event_time,
  location: p.location,
  published: p.published,
});

/** Dati da salvare, oppure il primo problema da mostrare. */
function toInput(d: Draft, current: Post | null): PostInput | string {
  if (!d.title.trim()) return 'Dai un titolo al contenuto';
  const slug = recipeSlug(d.slug || d.title);
  if (!slug) return "L'indirizzo della pagina deve contenere lettere o numeri";
  const isEvent = d.kind === 'evento';
  const bodyBlank = isRichTextBlank(d.body);
  if (isEvent) {
    if (!d.event_date) return "Indica il giorno dell'evento";
    if (d.event_end_date && d.event_end_date < d.event_date) return "L'ultimo giorno non può venire prima del primo";
  }
  if (d.published) {
    if (isEvent && !d.location.trim()) return "Per pubblicare l'evento indica il luogo";
    if (isEvent && bodyBlank && !d.summary.trim()) return "Per pubblicare l'evento scrivi la presentazione o il testo";
    if (!isEvent && bodyBlank) return 'Per pubblicare scrivi il testo';
  }
  return {
    kind: d.kind,
    title: d.title.trim(),
    slug,
    summary: d.summary.trim(),
    image_url: d.image_url,
    body: bodyBlank ? '' : d.body,
    event_date: isEvent ? d.event_date : null,
    event_end_date: isEvent && d.event_end_date && d.event_end_date !== d.event_date ? d.event_end_date : null,
    event_time: isEvent ? d.event_time.trim() : '',
    location: isEvent ? d.location.trim() : '',
    published: d.published,
    // La data resta quella della prima pubblicazione, anche se si ripubblica.
    published_at: current?.published_at ?? (d.published ? new Date().toISOString() : null),
  };
}

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-xs font-semibold text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="text-xs leading-5 text-muted-foreground">{hint}</span>}
  </label>
);

/** Titolo e introduzione della pagina /novita (site_content, riga page.posts). */
const PostsPageSettings: React.FC<Props> = ({ executeProtectedAction }) => {
  const { content } = useSiteContent('page.posts');
  const { logUpdateAction } = useAuditLog();
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(content), [content]);
  const dirty = draft.title !== content.title || draft.intro !== content.intro;

  const save = async () => {
    const value = { title: draft.title.trim(), intro: draft.intro.trim() };
    if (!value.title || !value.intro) {
      toast.error('Titolo e introduzione non possono essere vuoti');
      return;
    }
    setSaving(true);
    await executeProtectedAction(async () => {
      try {
        const before = savedSiteContent('page.posts');
        await saveSiteContent('page.posts', value);
        await logUpdateAction('site_content', 'page.posts', { content: before }, { content: value });
        toast.success('Pagina eventi e novità salvata');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(false);
  };

  return (
    <section className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-5">
        <div>
          <h2 className="font-display text-xl font-semibold text-foreground">Pagina eventi e novità</h2>
          <p className="mt-1 text-sm leading-[22px] text-muted-foreground">Il titolo e il testo sopra l'elenco di eventi e novità.</p>
        </div>
        <a
          href="/novita"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline"
        >
          Vedi sul sito
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <div className="flex flex-col gap-4 px-6 py-5">
        <Field label="Titolo">
          <input className={inputClass} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        </Field>
        <Field label="Introduzione">
          <textarea rows={2} className={textareaClass} value={draft.intro} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} />
        </Field>
        <button type="button" onClick={save} disabled={!dirty || saving} className={`${primaryButton} self-end`}>
          {saving ? 'Salvo…' : 'Salva'}
        </button>
      </div>
    </section>
  );
};

/** Vista Contenuti → Eventi e novità: elenco, creazione, modifica ed eliminazione. */
const PostsEditor: React.FC<Props> = ({ executeProtectedAction }) => {
  const { posts, loading, error, savePost, deletePost } = usePosts();
  const { logCreateAction, logUpdateAction, logDeleteAction } = useAuditLog();
  // null = elenco; 'new' = nuovo contenuto; altrimenti l'id di quello aperto.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const current = editing && editing !== 'new' ? posts.find((p) => p.id === editing) ?? null : null;
  const dirty = editing !== null && JSON.stringify(draft) !== JSON.stringify(current ? toDraft(current) : emptyDraft);
  const isEvent = draft.kind === 'evento';

  // Chi chiude la scheda con modifiche non salvate riceve l'avviso del browser.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const open = (post: Post | null) => {
    setDraft(post ? toDraft(post) : emptyDraft);
    setEditing(post ? post.id : 'new');
  };

  const close = () => {
    if (dirty && !window.confirm('Hai modifiche non salvate. Chiudere comunque?')) return;
    setEditing(null);
  };

  const set = (changes: Partial<Draft>) =>
    setDraft((prev) => {
      const next = { ...prev, ...changes };
      if ('title' in changes && !prev.slugTouched) next.slug = recipeSlug(changes.title ?? '');
      return next;
    });

  const save = async () => {
    const input = toInput(draft, current);
    if (typeof input === 'string') {
      toast.error(input);
      return;
    }
    setSaving(true);
    await executeProtectedAction(async () => {
      try {
        const saved = await savePost(current?.id ?? null, input);
        if (current) await logUpdateAction('posts', saved.id, current, input);
        else await logCreateAction('posts', saved.id, input);
        setDraft(toDraft(saved));
        setEditing(saved.id);
        toast.success(saved.published ? 'Salvato e pubblicato' : 'Bozza salvata: non è ancora visibile sul sito');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(false);
  };

  const remove = async () => {
    setConfirmDelete(false);
    if (!current) return;
    setSaving(true);
    await executeProtectedAction(async () => {
      try {
        await deletePost(current.id);
        await logDeleteAction('posts', current.id, current);
        setEditing(null);
        toast.success('Contenuto eliminato');
        return true;
      } catch (err: any) {
        toast.error(err.message);
        return false;
      }
    });
    setSaving(false);
  };

  if (editing === null) {
    return (
      <div className="flex flex-col gap-6">
        <PostsPageSettings executeProtectedAction={executeProtectedAction} />

        <section className={cardClass}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-5">
            <div>
              <h2 className="font-display text-xl font-semibold text-foreground">Eventi e novità</h2>
              <p className="mt-1 text-sm leading-[22px] text-muted-foreground">
                Le bozze le vedi solo tu: sul sito compaiono quando le pubblichi.
              </p>
            </div>
            <button type="button" onClick={() => open(null)} className={`${primaryButton} flex items-center gap-1.5`}>
              <Plus className="h-4 w-4" />
              Nuovo contenuto
            </button>
          </div>

          {loading && <div className="px-6 py-5 text-sm text-muted-foreground">Caricamento…</div>}
          {error && <div className="px-6 py-5 text-sm text-danger">{error}</div>}
          {!loading && !error && posts.length === 0 && (
            <div className="px-6 py-10 text-center">
              <CalendarDays className="mx-auto mb-3 h-8 w-8 text-primary" />
              <p className="font-semibold text-foreground">Aggiungi il primo evento o la prima novità</p>
              <p className="mt-1 text-sm text-muted-foreground">Un mercatino, il nuovo raccolto, un racconto dall'apiario.</p>
            </div>
          )}

          {posts.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => open(p)}
              className="flex w-full items-center gap-4 border-b border-border px-6 py-3 text-left last:border-b-0 hover:bg-muted/40"
            >
              {p.image_url ? (
                <img src={p.image_url} alt="" className="h-12 w-16 flex-none rounded-md object-cover" />
              ) : (
                <div className="flex h-12 w-16 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                  {p.kind === 'evento' ? <CalendarDays className="h-5 w-5" /> : <Newspaper className="h-5 w-5" />}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{p.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {POST_KIND_LABELS[p.kind]} · {postDateLabel(p)}
                </div>
              </div>
              <span
                className="flex-none rounded-full px-3 py-1 text-[11px] font-bold tracking-wide"
                style={
                  p.published
                    ? { background: 'hsl(var(--eco))', color: 'hsl(var(--eco-foreground))' }
                    : { background: 'hsl(var(--secondary))', color: 'hsl(var(--muted-foreground))' }
                }
              >
                {p.published ? 'PUBBLICATO' : 'BOZZA'}
              </span>
            </button>
          ))}
        </section>
      </div>
    );
  }

  return (
    <section className={cardClass}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-5">
        <div className="min-w-0">
          <button type="button" onClick={close} className="mb-2 text-[13px] font-semibold text-primary hover:underline">
            ← Tutti gli eventi e le novità
          </button>
          <h2 className="font-display text-xl font-semibold text-foreground">{current ? current.title : 'Nuovo contenuto'}</h2>
        </div>
        {current?.published && (
          <a
            href={`/novita/${current.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[13px] font-semibold text-primary hover:underline"
          >
            Vedi sul sito
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="flex flex-col gap-5 px-6 py-5">
        <Field label="Tipo" hint="Gli eventi futuri vanno in cima alla pagina e in home; finita la data passano tra gli eventi passati.">
          <select className={inputClass} value={draft.kind} onChange={(e) => set({ kind: e.target.value as PostKind })}>
            {(Object.keys(POST_KIND_LABELS) as PostKind[]).map((kind) => (
              <option key={kind} value={kind}>
                {POST_KIND_LABELS[kind]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Titolo">
          <input
            className={inputClass}
            value={draft.title}
            placeholder={isEvent ? 'Mercatino di Natale a Portobuffolè' : 'Il raccolto di questa stagione'}
            onChange={(e) => set({ title: e.target.value })}
          />
        </Field>
        <Field label="Indirizzo della pagina" hint="Si compila dal titolo. Se lo cambi dopo la pubblicazione, il vecchio link smette di funzionare.">
          <div className="flex items-center overflow-hidden rounded-lg border border-border bg-card">
            <span className="flex h-10 items-center bg-muted px-3 text-sm text-muted-foreground">/novita/</span>
            <input
              className="h-10 min-w-0 flex-1 bg-transparent px-3 text-sm text-foreground focus:outline-none"
              value={draft.slug}
              onChange={(e) => set({ slug: e.target.value.toLowerCase(), slugTouched: true })}
              onBlur={() => set({ slug: recipeSlug(draft.slug) })}
            />
          </div>
        </Field>
        {isEvent && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Giorno">
                <input type="date" className={inputClass} value={draft.event_date} onChange={(e) => set({ event_date: e.target.value })} />
              </Field>
              <Field label="Ultimo giorno" hint="Solo se dura più giorni.">
                <input type="date" className={inputClass} value={draft.event_end_date} min={draft.event_date || undefined} onChange={(e) => set({ event_end_date: e.target.value })} />
              </Field>
              <Field label="Orario">
                <input className={inputClass} value={draft.event_time} placeholder="9:00 - 18:00" onChange={(e) => set({ event_time: e.target.value })} />
              </Field>
            </div>
            <Field label="Luogo">
              <input className={inputClass} value={draft.location} placeholder="Piazza Vittorio Emanuele, Portobuffolè (TV)" onChange={(e) => set({ location: e.target.value })} />
            </Field>
          </>
        )}
        <Field label="Presentazione" hint="Una o due frasi: compaiono nella scheda e su Google.">
          <textarea rows={2} className={textareaClass} value={draft.summary} onChange={(e) => set({ summary: e.target.value })} />
        </Field>
        <ImageField
          label="Foto"
          value={draft.image_url}
          prefix={`post-${draft.slug || 'nuovo'}`}
          hint="Orizzontale, almeno 1200 px di larghezza, massimo 5 MB."
          onChange={(image_url) => set({ image_url })}
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-muted-foreground">{isEvent ? 'Testo (facoltativo)' : 'Testo'}</span>
          <RichTextField label="Testo del contenuto" value={draft.body} onChange={(body) => set({ body })} />
        </div>
        <label className="flex items-center gap-3 rounded-lg bg-muted/50 px-4 py-3">
          <input type="checkbox" className="h-4 w-4 accent-primary" checked={draft.published} onChange={(e) => set({ published: e.target.checked })} />
          <span className="text-sm">
            <span className="font-semibold text-foreground">Pubblicato sul sito</span>
            <span className="block text-xs text-muted-foreground">Togli la spunta per tenerlo in bozza.</span>
          </span>
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-muted/30 px-6 py-4">
        {current ? (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={saving}
            className="h-10 rounded-lg px-3 text-[13px] font-semibold text-danger hover:bg-danger/10"
          >
            Elimina
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button type="button" onClick={close} disabled={saving} className={ghostButton}>
            {dirty ? 'Annulla' : 'Chiudi'}
          </button>
          <button type="button" onClick={save} disabled={saving || (!dirty && !!current)} className={primaryButton}>
            {saving ? 'Salvo…' : draft.published ? 'Salva e pubblica' : 'Salva bozza'}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title={`Eliminare "${current?.title}"?`}
        message="Il contenuto sparisce dal sito e non si può recuperare. Se vuoi solo nasconderlo, togli la spunta Pubblicato."
        confirmText="Elimina"
        type="danger"
      />
    </section>
  );
};

export default PostsEditor;
